/* Slideshow motion: Ken Burns zoom + custom slide transition timing. */
const TRIM = { soft: 0, balanced: 0.1, crisp: 0.2 };
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);

function setupTransition(slides, duration, easing) {
  if (slides.__motion || easing === 'browser') return;
  slides.__motion = true;
  const native = slides.scrollTo.bind(slides);
  const trim = TRIM[easing] ?? 0.1;
  let raf = 0;
  let saved = null;
  const finish = () => {
    cancelAnimationFrame(raf);
    raf = 0;
    if (saved) {
      slides.style.scrollSnapType = saved.snap;
      slides.style.scrollBehavior = saved.behavior;
      saved = null;
    }
  };
  ['touchstart', 'wheel', 'pointerdown'].forEach((e) => slides.addEventListener(e, finish, { passive: true }));
  slides.scrollTo = function (opts, y) {
    if (!opts || typeof opts !== 'object' || opts.behavior !== 'smooth' || reduced()) {
      finish();
      return native(opts, y);
    }
    const left = opts.left;
    if (typeof left !== 'number') return native(opts, y);
    const from = slides.scrollLeft;
    if (Math.abs(left - from) < 1) return;
    if (!saved) saved = { snap: slides.style.scrollSnapType, behavior: slides.style.scrollBehavior };
    cancelAnimationFrame(raf);
    slides.style.scrollSnapType = 'none';
    slides.style.scrollBehavior = 'auto';
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const p = ease(Math.min(1, t / (1 - trim)));
      slides.scrollLeft = from + (left - from) * p;
      if (t < 1 && p < 1) raf = requestAnimationFrame(step);
      else {
        slides.scrollLeft = left;
        finish();
      }
    };
    raf = requestAnimationFrame(step);
  };
}

function setupKenBurns(root, mode) {
  if (mode === 'off' || reduced()) return;
  /* Safety: sanitize zoom scale (valid range 1.01 - 1.25, fallback 1.08) */
  let scale = parseFloat(getComputedStyle(root).getPropertyValue('--kb-scale'));
  if (!(scale >= 1.01 && scale <= 1.25)) scale = 1.08;
  root.style.setProperty('--kb-scale', String(scale));
  let n = 0;
  const apply = (slide) => {
    const target = slide.querySelector('.slide__image-container');
    if (!target) return;
    let dir = mode;
    if (mode === 'alternate') dir = n % 2 === 0 ? 'in' : 'out';
    else if (mode === 'random') dir = Math.random() < 0.5 ? 'in' : 'out';
    n++;
    target.removeAttribute('data-kb');
    void target.offsetWidth;
    target.setAttribute('data-kb', dir);
  };
  const all = () => root.querySelectorAll('slideshow-slide');
  const obs = new MutationObserver((muts) => {
    muts.forEach((m) => {
      const s = m.target;
      if (s.getAttribute('aria-hidden') === 'false' && m.oldValue !== 'false') apply(s);
    });
  });
  all().forEach((s) => {
    obs.observe(s, { attributes: true, attributeFilter: ['aria-hidden'], attributeOldValue: true });
    if (s.getAttribute('aria-hidden') !== 'true') apply(s);
  });
}

function init(root) {
  if (root.__motionInit) return;
  root.__motionInit = true;
  const slides = root.querySelector('slideshow-slides');
  if (slides) setupTransition(slides, Number(root.dataset.scrollDuration) || 700, root.dataset.scrollEasing || 'balanced');
  setupKenBurns(root, root.dataset.kbMode || 'off');
}

const run = () => document.querySelectorAll('slideshow-component[data-kb-mode]').forEach(init);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
else run();
document.addEventListener('shopify:section:load', () => setTimeout(run, 0));
